import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Globe, Send, CheckCircle2, Phone } from "lucide-react";

const SERVICE_SUBJECTS = [
  "Personal & Executive Protection",
  "Commercial Security Services",
  "Residential Security",
  "Event Security",
  "Armed Security Personnel",
  "Unarmed Security Personnel",
  "Mobile Patrol Services",
  "CCTV Monitoring & Surveillance",
  "Logistics Escort & Security",
  "Nightlife & Venue Security",
  "Corporate Security Consulting",
];
const OTHER = "Other (specify below)";
const REGIONS = ["Abuja · FCT", "Lagos", "Port Harcourt"];
const empty = { name: "", email: "", phone: "+234 ", subject: SERVICE_SUBJECTS[0], otherSubject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const finalSubject = form.subject === OTHER
      ? (form.otherSubject.trim() || "Website enquiry")
      : form.subject;
    try {
      const { data } = await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: finalSubject,
        message: form.message,
      });
      toast.success(`Message sent to ${data.delivered_to} — a confirmation has been emailed to you.`);
      setSent(true);
      setForm(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy">
      <ContactHeader />
      <ContactHero />
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 s-card" style={{ padding: 32 }}>
            {sent
              ? <SentState onAnother={() => setSent(false)} />
              : <ContactForm form={form} setForm={setForm} sending={sending} onSubmit={submit} />}
          </div>
          <ContactSidebar />
        </div>
      </section>
      <ContactFooter />
    </div>
  );
};

const ContactHeader = () => (
  <header style={{ borderBottom: "1px solid var(--border-dark)" }}>
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
      <Logo size={64} />
      <div className="flex items-center gap-5">
        <Link to="/" className="text-muted hover:text-white" style={{ fontSize: 14 }}>Home</Link>
        <Link to="/about" className="text-muted hover:text-white" style={{ fontSize: 14 }}>About us</Link>
        <Link to="/login" className="btn-outline">Sign in</Link>
      </div>
    </div>
  </header>
);

const ContactHero = () => (
  <section className="max-w-6xl mx-auto px-6 pt-16 pb-12">
    <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
      Get in touch
    </div>
    <h1 style={{ fontSize: 48, lineHeight: 1.1, fontWeight: 500, letterSpacing: "-0.02em", maxWidth: 820 }}>
      Talk to our operations team.
    </h1>
    <p className="text-muted mt-6" style={{ fontSize: 17, lineHeight: 1.6, maxWidth: 640 }}>
      Whether you need a single VIP detail, a multi-site guard contract, or a tactical
      consultation — send us a note and a member of the Sirod Secur team will respond
      within one business day.
    </p>
  </section>
);

const SentState = ({ onAnother }) => (
  <div className="text-center" style={{ padding: "32px 16px" }} data-testid="contact-sent">
    <div style={{
      width: 64, height: 64, borderRadius: "50%", margin: "0 auto 16px",
      background: "rgba(45, 158, 95, 0.15)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <CheckCircle2 size={28} color="#2D9E5F" />
    </div>
    <h2 className="h-section" style={{ fontSize: 22 }}>Message received</h2>
    <p className="text-muted mt-2" style={{ fontSize: 14 }}>
      Your enquiry has been forwarded to our operations team and a
      confirmation email has been sent to your inbox. We'll be in touch shortly.
    </p>
    <button onClick={onAnother} className="btn-outline mt-6">
      Send another message
    </button>
  </div>
);

const ContactForm = ({ form, setForm, sending, onSubmit }) => (
  <form onSubmit={onSubmit}>
    <div className="h-section mb-5" style={{ fontSize: 18 }}>Send us a message</div>

    <div className="grid md:grid-cols-2 gap-4 mb-4">
      <Field label="Full name">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
               className="s-input mt-1" data-testid="contact-name" />
      </Field>
      <Field label="Email">
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
               className="s-input mt-1" data-testid="contact-email" />
      </Field>
    </div>

    <div className="grid md:grid-cols-2 gap-4 mb-4">
      <Field label="Phone (+234)">
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
               className="s-input mt-1" data-testid="contact-phone" />
      </Field>
      <Field label="Subject — service of interest">
        <select
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          className="s-input mt-1"
          data-testid="contact-subject"
        >
          {SERVICE_SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
          <option value={OTHER}>{OTHER}</option>
        </select>
      </Field>
    </div>

    {form.subject === OTHER && (
      <div className="mb-4">
        <label className="text-muted" style={{ fontSize: 12 }}>Specify subject</label>
        <input
          required
          value={form.otherSubject}
          onChange={(e) => setForm({ ...form, otherSubject: e.target.value })}
          className="s-input mt-1"
          placeholder="Tell us what you need help with"
          data-testid="contact-subject-other"
        />
      </div>
    )}

    <label className="text-muted" style={{ fontSize: 12 }}>Message</label>
    <textarea required rows={6} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="s-input mt-1 mb-5"
              placeholder="Tell us about your security requirement — location, dates, scope..."
              data-testid="contact-message" />

    <button type="submit" disabled={sending}
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
            data-testid="contact-submit">
      <Send size={16} /> {sending ? "Sending..." : "Send message"}
    </button>
  </form>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-muted" style={{ fontSize: 12 }}>{label}</label>
    {children}
  </div>
);

const ContactSidebar = () => (
  <div className="lg:col-span-2 space-y-4">
    <SidebarCard title="Direct contact">
      <ContactRow icon={Mail} label="Email" value="admin@sirodsecur.com" href="mailto:admin@sirodsecur.com" />
      <ContactRow icon={Phone} label="Phone" value="080 9999 9999" href="tel:+2348099999999" />
      <ContactRow icon={Globe} label="Website" value="www.sirodsecur.com" href="https://www.sirodsecur.com" />
      <ContactRow icon={MapPin} label="Headquarters" value="Abuja, Nigeria" />
    </SidebarCard>

    <SidebarCard title="Operating regions">
      <div className="space-y-2">
        {REGIONS.map(r => (
          <div key={r} className="flex items-center gap-2" style={{ fontSize: 14 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2ECAC8" }} />
            {r}
          </div>
        ))}
      </div>
    </SidebarCard>

    <SidebarCard title="Emergencies">
      <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
        If you are a current client experiencing an active incident, contact the
        24/7 Sirod Secur operations centre directly via the number provided in your
        service agreement.
      </p>
    </SidebarCard>
  </div>
);

const SidebarCard = ({ title, children }) => (
  <div className="s-card" style={{ padding: 24 }}>
    <div className="text-teal" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
      {title}
    </div>
    {children}
  </div>
);

const ContactRow = ({ icon: Icon, label, value, href }) => {
  const inner = (
    <div className="flex items-start gap-3 py-2" style={{ borderBottom: "1px solid var(--border-dark)" }}>
      <Icon size={14} color="#8CA0B8" style={{ marginTop: 4 }} />
      <div>
        <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
        <div style={{ fontSize: 14, color: "#fff", marginTop: 2 }}>{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{inner}</a> : inner;
};

const ContactFooter = () => (
  <footer style={{ borderTop: "1px solid var(--border-dark)" }}>
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-muted" style={{ fontSize: 13 }}>
      <div>© 2026 Sirod Secur · Protection you can trust</div>
      <div>Headquarters · Abuja, Nigeria · admin@sirodsecur.com</div>
    </div>
  </footer>
);
